import { useCallback, useEffect, useRef, useState } from "react";

import {
  showEntri,
  close as closeEntri,
  type EntriConfig,
  type EntriSuccessEventDetail,
  type EntriCloseEventDetail,
} from "entrijs";

import type { EntriDnsRecord } from "@/lib/entri";

// Entri's SDK injects an iframe with this id and removes it on close. We poll for
// its removal as a SAFETY NET in case the `onEntriClose` window event doesn't fire.
const ENTRI_IFRAME_ID = "entriApp";
const ENTRI_POLL_MS = 400;

// Window events Entri dispatches — see https://developers.entri.com/connect/events.
// (`entrijs`'s config has no callbacks; events are the only contract.)
const ENTRI_SUCCESS_EVENT = "onSuccess";
const ENTRI_CLOSE_EVENT = "onEntriClose";

export interface UseEntriCallbacks {
  /** Fired when the user reaches Entri's success screen. `detail` has jobId + domain. */
  onSuccess?: (detail: EntriSuccessEventDetail) => void;
  /** Fired when the Entri modal closes (X, outside click, finish, or `close()`). */
  onClose?: (detail: EntriCloseEventDetail) => void;
}

export interface OpenEntriArgs {
  applicationId: string;
  token: string;
  /** Entri-shaped records (`{ type, host, value, ttl }`) — use `toEntriRecords()`. */
  dnsRecords: EntriDnsRecord[];
  /** Pre-fill the domain field. */
  prefilledDomain?: string;
  /** Echoed back in Entri webhook events — pass for backend correlation. */
  userId?: string;
}

export interface UseEntri {
  /** Launch the Entri one-click DNS modal. */
  open: (args: OpenEntriArgs) => Promise<void>;
  /** Programmatically dismiss the Entri modal. */
  close: () => void;
  /** True while the Entri modal is open (use to step your own UI aside). */
  isActive: boolean;
  /** True while the SDK is launching the modal. */
  isLoading: boolean;
}

/**
 * Reusable wrapper around the Entri (`entrijs`) one-click DNS SDK.
 *
 * Opens the Entri modal via `open()` and tracks `isActive` so callers can hide
 * their own overlay while Entri is up (Entri injects a full-screen iframe). The
 * reliable "closed" signal is Entri's `onEntriClose` window event; a DOM-watch on
 * the injected `#entriApp` iframe is kept as a safety net. Callbacks are read from
 * a ref so the window listeners register once and never go stale.
 */
export function useEntri(callbacks?: UseEntriCallbacks): UseEntri {
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Register the Entri window-event listeners once.
  useEffect(() => {
    const handleSuccess = (event: Event): void => {
      callbacksRef.current?.onSuccess?.(
        (event as CustomEvent<EntriSuccessEventDetail>).detail,
      );
    };
    const handleClose = (event: Event): void => {
      setIsActive(false);
      callbacksRef.current?.onClose?.(
        (event as CustomEvent<EntriCloseEventDetail>).detail,
      );
    };

    window.addEventListener(ENTRI_SUCCESS_EVENT, handleSuccess);
    window.addEventListener(ENTRI_CLOSE_EVENT, handleClose);
    return () => {
      window.removeEventListener(ENTRI_SUCCESS_EVENT, handleSuccess);
      window.removeEventListener(ENTRI_CLOSE_EVENT, handleClose);
    };
  }, []);

  // Safety net: if `onEntriClose` somehow doesn't fire, detect the iframe removal.
  useEffect(() => {
    if (!isActive) return;
    let appeared = false;
    const interval = window.setInterval(() => {
      const exists = !!document.getElementById(ENTRI_IFRAME_ID);
      if (exists) {
        appeared = true;
      } else if (appeared) {
        setIsActive(false);
      }
    }, ENTRI_POLL_MS);
    return () => window.clearInterval(interval);
  }, [isActive]);

  const open = useCallback(async (args: OpenEntriArgs): Promise<void> => {
    setIsLoading(true);
    try {
      const config: EntriConfig = {
        applicationId: args.applicationId,
        token: args.token,
        dnsRecords: args.dnsRecords,
        prefilledDomain: args.prefilledDomain,
        userId: args.userId,
      };
      await showEntri(config);
      setIsActive(true);
    } catch (error) {
      // SDK failed to launch — let the caller reveal its fallback.
      setIsActive(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const close = useCallback((): void => {
    void closeEntri();
    setIsActive(false);
  }, []);

  return { open, close, isActive, isLoading };
}
